import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';  // for reading query params
import { motion, AnimatePresence } from 'framer-motion';
import ListingCard from '../components/ListingCard';
import AddListingModal from '../components/AddListingModal';
import EditListingModal from '../components/EditListingModal';
import { refreshImageUrls, checkNeedsUrlRefresh } from '../services/api';

function AllPosts({ isAdmin, onLogout }) {
  const [allListings, setAllListings] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [listingToEdit, setListingToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(9);
  
  // 1) Grab the location object to read ?filter=xxx
  const location = useLocation();

  // Helper function to refresh image URLs for a listing
  const refreshImageUrls = async (listingId) => {
    try {
      console.log(`Refreshing image URLs for listing ${listingId}`);
      const response = await fetch(`https://eurocentrumv1.onrender.com/renew-image-urls/${listingId}`);
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const refreshedListing = await response.json();
      console.log(`Successfully refreshed image URLs for listing ${listingId}`);
      return refreshedListing;
    } catch (error) {
      console.error(`Error refreshing image URLs for listing ${listingId}:`, error);
      return null;
    }
  };

  useEffect(() => {
    // 2) On mount, parse the query string
    const searchParams = new URLSearchParams(location.search);
    const filterFromURL = searchParams.get('filter'); // e.g. "auto"
    
    // If the user passed a filter, set it in selectedFilters
    if (filterFromURL) {
      setSelectedFilters([filterFromURL]);
    } else {
      // Set a default filter if none is provided
      setSelectedFilters(['predaj']); // Default to 'predaj' when no filter is selected
    }

    // 3) Now fetch listings
    const fetchAll = async () => {
      try {
        setIsLoading(true);
        const res = await fetch('https://eurocentrumv1.onrender.com/listings');
        const data = await res.json();
        
        // More explicit filtering to ensure neplatiči posts are always excluded
        const filteredData = data.filter(listing => {
          // If listing has tags and one of them is "neplatiči", filter it out
          if (listing.tags && listing.tags.includes('neplatiči')) {
            return false;
          }
          // Otherwise keep it
          return true;
        });
        
        // Refresh image URLs for any listings that need it
        console.log("Starting to refresh image URLs for all listings");
        const refreshedListings = [];
        
        for (const listing of filteredData) {
          // Check if any images might need refresh
          if (checkNeedsUrlRefresh(listing)) {
            try {
              const refreshed = await refreshImageUrls(listing._id);
              refreshedListings.push(refreshed);
            } catch (error) {
              console.error(`Error refreshing URLs for ${listing._id}, using original listing`);
              refreshedListings.push(listing);
            }
          } else {
            refreshedListings.push(listing);
          }
        }
        
        setAllListings(refreshedListings);
        console.log("Finished refreshing image URLs for listings");
      } catch (error) {
        console.error('Error fetching all listings', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAll();
  }, [location.search]);

  // Re-fetch after an edit
  const refreshAll = async () => {
    try {
      const res = await fetch('https://eurocentrumv1.onrender.com/listings');
      const data = await res.json();
      
      // More explicit filtering to ensure neplatiči posts are always excluded
      const filteredData = data.filter(listing => {
        // If listing has tags and one of them is "neplatiči", filter it out
        if (listing.tags && listing.tags.includes('neplatiči')) {
          return false;
        }
        // Otherwise keep it
        return true;
      });
      
      // Refresh image URLs for any listings that need it
      console.log("Starting to refresh image URLs for listings after edit");
      const refreshedListings = [];
      
      for (const listing of filteredData) {
        // Always refresh images after an edit to ensure fresh URLs
        try {
          const refreshed = await refreshImageUrls(listing._id);
          refreshedListings.push(refreshed);
        } catch (error) {
          console.error(`Error refreshing URLs for ${listing._id}, using original listing`);
          refreshedListings.push(listing);
        }
      }
      
      setAllListings(refreshedListings);
      console.log("Finished refreshing image URLs after edit");
      
      // Clear the search
      setSelectedFilters([]);
    } catch (error) {
      console.error('Error refreshing listings', error);
    }
  };

  // availableFilters - updated to include all requested tags except neplatiči and aktuálne
  const availableFilters = ['auto', 'byt', 'dom', 'predaj', 'prenájom'];

  // handleFilterClick - updated to handle the absence of "all" option
  const handleFilterClick = (filter) => {
    setSelectedFilters((prev) => {
      if (prev.includes(filter)) {
        // If this is the only filter and it's being deselected, select a default filter
        if (prev.length === 1) {
          return ['predaj']; // Default to 'predaj' when all filters would be deselected
        }
        return prev.filter((f) => f !== filter);
      }
      return [...prev, filter];
    });
  };

  // Handle tag click from listing card
  const handleTagClick = (tag) => {
    if (tag !== 'neplatiči' && tag !== 'aktuálne' && availableFilters.includes(tag)) {
      setSelectedFilters([tag]);
    }
  };

  // Make sure the displayedListings applies the filters and excludes neplatiči
  const displayedListings = allListings
    .filter(listing => {
      // Never show neplatiči tagged posts
      if (listing.tags && listing.tags.includes('neplatiči')) {
        return false;
      }
      
      // Apply the selected filters
      return listing.tags?.some((tag) => selectedFilters.includes(tag));
    });

  // motion variants
  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 },
  };

  // Add listing
  const handleAddListing = (newListing) => {
    console.log("Adding new listing to AllPosts:", newListing);
    
    // Instead of forcing to top, just refresh all listings from server
    // which will respect the server-side sorting
    refreshAll();
    
    setShowAddModal(false);
  };

  // Handle editing a listing
  const handleEditClick = (listing) => {
    setListingToEdit(listing);
    setShowEditModal(true);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.heading}>Všetky inzeráty</h2>
        {isAdmin && (
          <button 
            style={styles.addButton}
            onClick={() => setShowAddModal(true)}
          >
            + Pridať nový inzerát
          </button>
        )}
      </div>

      <div style={styles.buttonRow}>
        {availableFilters.map((f) => (
          <button
            key={f}
            style={selectedFilters.includes(f) ? styles.activeButton : styles.filterButton}
            onClick={() => handleFilterClick(f)}
          >
            {f.toUpperCase()}
          </button>
        ))}
      </div>

      <div style={styles.listingsContainer}>
        {isLoading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p style={styles.loadingText}>Načítavam inzeráty...</p>
          </div>
        ) : (
          <AnimatePresence>
            {displayedListings.length > 0 ? (
              displayedListings.map((listing) => (
                <motion.div
                  key={listing._id}
                  layout
                  initial="hidden"
                  animate="visible"
                  exit={{ opacity: 0, scale: 0.9 }}
                  variants={itemVariants}
                  transition={{ duration: 0.3 }}
                >
                  <ListingCard
                    listing={listing}
                    isAdmin={isAdmin}
                    onRefresh={refreshAll}
                    onTagClick={handleTagClick}
                    onEditClick={handleEditClick}
                  />
                </motion.div>
              ))
            ) : (
              <div style={styles.noListings}>
                <h3>Žiadne inzeráty na zobrazenie</h3>
                <p>Momentálne nemáme žiadne inzeráty s vybranými filtrami.</p>
              </div>
            )}
          </AnimatePresence>
        )}
      </div>

      {/* Load More Button */}
      {!isLoading && visibleCount < allListings.length && (
        <div style={styles.loadMoreContainer}>
          <button
            onClick={() => setVisibleCount(prev => prev + 9)}
            style={styles.loadMoreButton}
          >
            Načítať viac
          </button>
        </div>
      )}

      {/* Add Listing Modal */}
      {showAddModal && isAdmin && (
        <AddListingModal
          onClose={() => setShowAddModal(false)}
          onRefresh={refreshAll}
          onAddListing={handleAddListing}
        />
      )}

      {/* Edit Listing Modal */}
      {showEditModal && isAdmin && (
        <EditListingModal
          listingToEdit={listingToEdit}
          onClose={() => {
            setShowEditModal(false);
            setListingToEdit(null);
          }}
          onRefresh={refreshAll}
        />
      )}
    </div>
  );
}

export default AllPosts;

const styles = {
  container: {
    fontFamily: 'sans-serif',
    margin: '80px auto',
    maxWidth: '1200px',
    padding: '1rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  heading: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  addButton: {
    backgroundColor: '#2c69a0',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.2rem',
    cursor: 'pointer',
    fontSize: '1rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '0.8rem',
    marginBottom: '2rem',
    padding: '1rem 0',
  },
  filterButton: {
    backgroundColor: '#f8f9fa',
    color: '#495057',
    border: '1px solid #dee2e6',
    borderRadius: '30px',
    padding: '0.7rem 1.5rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    minWidth: '120px',
    textAlign: 'center',
  },
  activeButton: {
    backgroundColor: 'rgb(44, 105, 160)',
    color: '#fff',
    border: '1px solid rgb(44, 105, 160)',
    borderRadius: '30px',
    padding: '0.7rem 1.5rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    minWidth: '120px',
    textAlign: 'center',
  },
  listingsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2rem',
    justifyContent: 'center',
    width: '100%',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '2rem',
  },
  loadingSpinner: {
    width: '50px',
    height: '50px',
    border: '5px solid rgba(44, 105, 160, 0.2)',
    borderTop: '5px solid rgb(44, 105, 160)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '1rem',
    color: '#666',
    fontSize: '1.1rem',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  noListings: {
    textAlign: 'center',
    padding: '2rem',
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '2rem',
  },
  loadMoreButton: {
    backgroundColor: '#2c69a0',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.2rem',
    cursor: 'pointer',
    fontSize: '1rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
};
